import type { DeadAirCategory } from "./deadAirTypes";

/**
 * Why a confident, fillable answer was still withheld. Reported as `gateReason` on
 * `DeadAirAssistSuppressed` so the dark launch keeps measuring what the gate drops.
 */
export type DeadAirAnswerGateRejection = "re-ask" | "destructive-action" | "off-kb-tool-path" | "not-device-how-to";

/**
 * ACSS flows the model reproduces near-verbatim across many calls, consistent with the Helix KB articles it
 * is given. In the 10-06..10-12 dark-launch review, every useful tool-path answer matched one of these;
 * tool paths outside them contradicted each other, sometimes within a single call.
 */
const KB_TOOL_FLOWS: readonly RegExp[] = [
    /service change[\s\S]*validate/i,
    /retry resolver/i,
    /regenerate qr/i,
    /port-in conf/i,
    /pending (?:orders|transactions)[\s\S]*activate/i,
    /international wizard|quick links\s*>\s*international/i,
    // The `>` breadcrumb keeps prose like "review the overview ... devices ... manage expectations" out.
    /overview\b[^\n]{0,20}>[^\n]{0,40}devices[^\n]{0,80}manage/i,
];

/** The answer names an internal agent tool, so it is a tool path rather than a consumer how-to. */
const AGENT_TOOL = /\bACSS\b|expert workspace|\bTRG\b|order ?track/i;

/** Markers of a consumer device/app how-to, the other kind of answer the review found useful. */
const DEVICE_HOW_TO =
    /\bsettings\b|\bpress and hold\b|\b(?:app|play) store\b|\b(?:power|volume|side) button\b|\bwatch app\b/i;

/**
 * Irreversible or account-changing actions. Every risky answer in the review that was not a misread
 * silence told the expert to cancel, delete, remove, or reset something, and the expert may act on it.
 * "remove" only counts when it targets account state, so a physical step ("remove the SIM tray") passes.
 * Routine troubleshooting resets (network settings, restarts) are deliberately not here: blocking them
 * dropped useful answers in the replay without removing any risky ones.
 */
const DESTRUCTIVE_ACTION =
    /\b(?:cancel|delete|close (?:the )?account|disconnect|factory (?:data )?reset|erase|esn swap)\b|\bremove\b[^.\n]{0,25}\b(?:service|line|account|order|plan|feature|esim)\b/i;

/**
 * Decide whether a confident answer to a fillable silence is the kind that has proven useful, or return
 * why it is not. Only two shapes earned their place: a known KB-backed ACSS flow, and a consumer device
 * how-to for a lookup. RE_ASK is rejected outright: none of its answers were useful, because the quote was
 * usually the customer's last line or the expert's own question.
 */
export function getAnswerGateRejection(category: DeadAirCategory, answer: string): DeadAirAnswerGateRejection | null {
    if (category === "RE_ASK") return "re-ask";
    if (DESTRUCTIVE_ACTION.test(answer)) return "destructive-action";
    if (KB_TOOL_FLOWS.some((flow) => flow.test(answer))) return null;
    if (category !== "LOOKUP_STALL" || AGENT_TOOL.test(answer)) return "off-kb-tool-path";
    return DEVICE_HOW_TO.test(answer) ? null : "not-device-how-to";
}
